import React, { useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";

// Inline font-size. Saved as style="font-size: Npx", which email apps keep.
const Parchment = Quill.import("parchment");
const FontSize = new Parchment.Attributor.Style("fontsize", "font-size", { scope: Parchment.Scope.INLINE });
Quill.register(FontSize, true);

const SIZE_PRESETS = ["10", "12", "14", "16", "18", "20", "24", "28", "32", "36"];
const DEFAULT_SIZE = "14";
// After a size is applied, the editor's events still report the old size for a moment
let sizeHoldUntil = 0;

const icons = Quill.import("ui/icons");
icons.firstname = "{firstname}";
icons.lastname = "{lastname}";

function insertToken(token) {
  const range = this.quill.getSelection(true);
  this.quill.insertText(range.index, token, "user");
  this.quill.setSelection(range.index + token.length, 0, "user");
}

const MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ["link"],
      ["firstname", "lastname"],
    ],
    handlers: {
      firstname() { insertToken.call(this, "{firstname}"); },
      lastname() { insertToken.call(this, "{lastname}"); },
    },
  },
  clipboard: { matchVisual: false },
};
// "link" keeps links pasted into the content (they become tracked links on send)
const FORMATS = ["fontsize", "header", "bold", "italic", "underline", "strike", "script", "list", "bullet",
  "indent", "align", "color", "background", "link"];

const TITLES = {
  ".ql-header": "Heading", ".ql-bold": "Bold", ".ql-italic": "Italic",
  ".ql-underline": "Underline", ".ql-strike": "Strikethrough", ".ql-script[value='sub']": "Subscript",
  ".ql-script[value='super']": "Superscript", ".ql-list[value='ordered']": "Numbered list",
  ".ql-list[value='bullet']": "Bullet list", ".ql-indent[value='-1']": "Decrease indent",
  ".ql-indent[value='+1']": "Increase indent", ".ql-align": "Alignment", ".ql-color": "Text colour",
  ".ql-background": "Highlight colour", ".ql-link": "Add a link (becomes a tracked link on send)",
  ".ql-firstname": "Each recipient's first name (shown as {firstname} here, the real name in the preview and the email)",
  ".ql-lastname": "Each recipient's last name (shown as {lastname} here, the real name in the preview and the email)",
};

const Wrap = styled.div`
  .ql-toolbar.ql-snow { border: 0; border-bottom: 1px solid var(--border); background: var(--surface-2); }
  .ql-toolbar button.ql-firstname, .ql-toolbar button.ql-lastname { width: auto; font-size: 12.5px; padding: 3px 6px; color: var(--primary-text); }
  .ce-size-wrap { position: relative; display: inline-flex; align-items: center; margin-right: 8px; vertical-align: middle; padding: 0 6px; border: 1px solid var(--border); border-radius: 4px; background: var(--surface); }
  .ce-size-wrap .ce-size { width: auto !important; min-width: 2ch; field-sizing: content; padding: 2px 0; font-size: 13px; border: 0; outline: none; background: transparent; color: inherit; text-align: left; }
  .ce-size-unit { font-size: 12px; color: var(--muted); padding-left: 2px; }
  .ce-size-list { position: absolute; top: 100%; left: 0; z-index: 1000; margin: 2px 0 0; padding: 4px 0; list-style: none; min-width: 96px; max-height: 240px; overflow-y: auto; background: var(--surface); border: 1px solid var(--border); border-radius: 6px; box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12); }
  .ce-size-list li { padding: 4px 12px; font-size: 13px; cursor: pointer; white-space: nowrap; }
  .ce-size-list li:hover { background: var(--surface-2); }
`;

// Font size box: click it to open the preset list, or type any size.
const addSizeBox = (quill, toolbar) => {
  const wrap = document.createElement("span");
  wrap.className = "ce-size-wrap";

  const input = document.createElement("input");
  input.type = "text";
  input.inputMode = "decimal";
  input.className = "ce-size";
  input.value = DEFAULT_SIZE;
  input.title = "Font size in pixels: click to pick, or type any size";
  input.setAttribute("aria-label", "Font size in pixels");
  // The box grows and shrinks with the number typed in it
  const fit = () => { input.size = Math.max(2, input.value.length); };
  fit();

  const panel = document.createElement("ul");
  panel.className = "ce-size-list";
  panel.hidden = true;

  const apply = () => {
    const px = parseFloat(input.value);
    panel.hidden = true;
    if (!(px >= 1 && px <= 200)) return;
    sizeHoldUntil = Date.now() + 400;
    if (quill.getSelection(true)) quill.format("fontsize", `${px}px`, "user");
  };

  SIZE_PRESETS.forEach((s) => {
    const item = document.createElement("li");
    item.textContent = `${s}px`;
    item.addEventListener("mousedown", (e) => e.preventDefault());
    item.addEventListener("click", () => {
      input.value = s;
      fit();
      apply();
    });
    panel.appendChild(item);
  });

  // Only digits and a decimal point are allowed
  input.addEventListener("input", () => { input.value = input.value.replace(/[^0-9.]/g, ""); fit(); });
  input.addEventListener("click", () => { panel.hidden = false; });
  input.addEventListener("focus", () => { panel.hidden = false; });
  input.addEventListener("change", apply);
  input.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    apply();
  });
  document.addEventListener("mousedown", (e) => { if (!wrap.contains(e.target)) panel.hidden = true; });

  // Show the size of the text under the cursor or selection
  const sync = (range) => {
    if (!range || Date.now() < sizeHoldUntil) return;
    const size = quill.getFormat(range).fontsize;
    input.value = size ? String(parseFloat(size)) : DEFAULT_SIZE;
    fit();
  };
  quill.on("selection-change", sync);
  quill.on("text-change", () => sync(quill.getSelection()));

  const unit = document.createElement("span");
  unit.className = "ce-size-unit";
  unit.textContent = "px";

  wrap.append(input, unit, panel);
  toolbar.prepend(wrap);
};

// Each editor gets its own id so Quill can keep its link box inside that editor
let editorSeq = 0;

/** Rich text for an "HTML editor" variable. */
const HtmlEditor = ({ value, onChange, ariaLabel }) => {
  // Quill positions the link box against document.body by default, so near the left edge it
  // slid out of the scrolling column and was cut off; `bounds` keeps it inside this editor.
  const id = useRef(`ce-editor-${++editorSeq}`).current;
  const setTitles = (el) => {
    const quill = el?.getEditor?.();
    const toolbar = quill?.getModule("toolbar")?.container;
    if (!toolbar) return;
    Object.entries(TITLES).forEach(([sel, title]) => toolbar.querySelectorAll(sel).forEach((b) => b.setAttribute("title", title)));
    if (!toolbar.querySelector(".ce-size-wrap")) addSizeBox(quill, toolbar);
  };
  return (
    <Wrap className="editor" id={id}>
      <ReactQuill ref={setTitles} theme="snow" value={value || ""} onChange={(html) => onChange(html)}
        modules={MODULES} formats={FORMATS} bounds={`#${id}`} aria-label={ariaLabel} />
    </Wrap>
  );
};

export default HtmlEditor;
